import type { DomainConfiguration } from './ConfigurationMapper';
import type { SystemConfiguration } from './SystemConfiguration';
import type { Pricing } from './Pricing';
import type { QuoteMetadata } from './QuoteMetadata';
import type { ViewState } from './ViewState';
import { CURRENT_SCHEMA_VERSION } from './schemaVersion';

// The structured `configV2` I/O shape. Boxes pass through 1:1; the open-ended
// unknownFields bag is serialized to AWSJSON (a JSON string) because GraphQL
// cannot type it. Mirrors ConfigurationMapper one level up.
export interface QuoteConfigIO {
  system: SystemConfiguration;
  pricing: Pricing;
  metadata: QuoteMetadata;
  viewState: ViewState;
  unknownFields: string | null;
}

export function domainToQuoteConfig(d: DomainConfiguration): QuoteConfigIO {
  return {
    system: d.system,
    pricing: d.pricing,
    metadata: d.metadata,
    viewState: d.viewState,
    unknownFields: JSON.stringify(d.unknownFields ?? {}),
  };
}

// AppSync attaches __typename to every nested object it returns. Strip it
// recursively so a reconstructed domain deep-equals a freshly-built one.
function stripTypename<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((v) => stripTypename(v)) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (k === '__typename') continue;
      out[k] = stripTypename(v);
    }
    return out as T;
  }
  return value;
}

export function quoteConfigToDomain(q: QuoteConfigIO): DomainConfiguration {
  const clean = stripTypename(q);
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    system: clean.system,
    pricing: clean.pricing,
    metadata: clean.metadata,
    viewState: clean.viewState,
    unknownFields: clean.unknownFields ? JSON.parse(clean.unknownFields) : {},
  };
}
